import React from 'react';
import { Button, Card, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  BHAComponent,
  StepProps,
  calcBHACumulative,
  createDefaultBHAComponent
} from '../types/types';

const Step4BHA: React.FC<StepProps> = ({ reportData, onChange }) => {
  const cumulativeLengths = calcBHACumulative(reportData.bhaComponents);
  const totalBHALength = cumulativeLengths[cumulativeLengths.length - 1] ?? 0;

  const updateComponent = (
    index: number,
    field: keyof BHAComponent,
    value: any
  ) => {
    const updated = reportData.bhaComponents.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    onChange({ bhaComponents: updated });
  };

  const addComponent = () => {
    if (reportData.bhaComponents.length >= 25) return;
    const next = createDefaultBHAComponent(reportData.bhaComponents.length + 1);
    onChange({ bhaComponents: [...reportData.bhaComponents, next] });
  };

  const removeComponent = (index: number) => {
    if (reportData.bhaComponents.length <= 1) return;
    onChange({
      bhaComponents: reportData.bhaComponents
        .filter((_, i) => i !== index)
        .map((c, i) => ({ ...c, itemNumber: i + 1 }))
    });
  };

  const numInput = (
    index: number,
    field: keyof BHAComponent,
    placeholder = '0',
    step = 'any'
  ) => (
    <Form.Control
      size="sm"
      type="number"
      step={step}
      placeholder={placeholder}
      className="pjr-bha-input"
      value={reportData.bhaComponents[index][field] as any}
      onChange={e =>
        updateComponent(
          index,
          field,
          e.target.value === '' ? '' : Number(e.target.value)
        )
      }
      style={{ minWidth: 60, padding: '0.2rem 0.35rem', fontSize: '0.8rem' }}
    />
  );

  const textInput = (
    index: number,
    field: keyof BHAComponent,
    placeholder = '',
    width = 90
  ) => (
    <Form.Control
      size="sm"
      type="text"
      placeholder={placeholder}
      value={reportData.bhaComponents[index][field] as string}
      onChange={e => updateComponent(index, field, e.target.value)}
      style={{ minWidth: width, padding: '0.2rem 0.35rem', fontSize: '0.8rem' }}
    />
  );

  return (
    <Card className="pjr-section-card">
      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
        <div>
          <span className="fw-semibold fs-9 text-uppercase">
            Bottom Hole Assembly
          </span>
          <span className="ms-3 badge bg-primary fs-10">
            {reportData.bhaComponents.length} components
          </span>
          <span className="ms-2 badge bg-success fs-10">
            Total: {totalBHALength.toFixed(2)} ft
          </span>
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={addComponent}
          disabled={reportData.bhaComponents.length >= 25}
        >
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          Add Component
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        <div style={{ overflowX: 'auto' }}>
          <Table className="pjr-bha-table mb-0" bordered hover size="sm">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th style={{ minWidth: 110 }}>Component</th>
                <th style={{ minWidth: 90 }}>Mfg</th>
                <th style={{ minWidth: 110 }}>Serial #</th>
                <th style={{ minWidth: 60 }}>OD</th>
                <th style={{ minWidth: 60 }}>ID</th>
                <th style={{ minWidth: 70 }}>F/N OD</th>
                <th style={{ minWidth: 70 }}>F/N Len</th>
                <th style={{ minWidth: 90 }}>Top Conn</th>
                <th style={{ minWidth: 90 }}>Bot Conn</th>
                <th style={{ minWidth: 70 }}>Length (ft)</th>
                <th className="pjr-cumulative-cell" style={{ minWidth: 80 }}>
                  Cumul (ft)
                </th>
                <th style={{ width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {reportData.bhaComponents.map((comp, idx) => (
                <tr key={idx}>
                  <td className="text-center fw-semibold text-muted">
                    {comp.itemNumber}
                  </td>
                  <td>{textInput(idx, 'component', 'e.g. BIT', 100)}</td>
                  <td>{textInput(idx, 'mfg', '', 80)}</td>
                  <td>{textInput(idx, 'serialNumber', '', 100)}</td>
                  <td>{numInput(idx, 'od', '0', '0.01')}</td>
                  <td>{numInput(idx, 'id', '0', '0.01')}</td>
                  <td>{numInput(idx, 'fishingNeckOD', '0', '0.01')}</td>
                  <td>{numInput(idx, 'fishingNeckLength', '0', '0.01')}</td>
                  <td>{textInput(idx, 'topConnection', '', 80)}</td>
                  <td>{textInput(idx, 'bottomConnection', '', 80)}</td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={comp.length}
                      onChange={e =>
                        updateComponent(
                          idx,
                          'length',
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                      style={{
                        minWidth: 65,
                        padding: '0.2rem 0.35rem',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}
                    />
                  </td>
                  <td className="pjr-cumulative-cell text-center">
                    {cumulativeLengths[idx].toFixed(2)}
                  </td>
                  <td className="text-center">
                    {reportData.bhaComponents.length > 1 && (
                      <Button
                        size="sm"
                        variant="link"
                        className="text-danger p-0"
                        onClick={() => removeComponent(idx)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="fs-10" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="table-secondary fw-bold">
                <td colSpan={10} className="text-end pe-2 fs-9">
                  Total BHA Length:
                </td>
                <td colSpan={2} className="pjr-cumulative-cell text-center">
                  {totalBHALength.toFixed(2)} ft
                </td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        </div>
        <div className="px-3 py-2 text-muted fs-10">
          F/N = Fishing Neck &nbsp;|&nbsp; Cumulative length updates
          automatically as you enter component lengths.
        </div>
      </Card.Body>
    </Card>
  );
};

export default Step4BHA;
